import React, { FC, useState } from 'react'
import classNames from 'classnames'
import {
  Box,
  LinkOverlay,
  Tab,
  TabList,
  TabPanels,
  Tabs
} from 'front-end/src/components'
import { brandColors } from 'shared/basicPalette'
import { TabsProps } from '@chakra-ui/react'

import './TabsLegacy.less'
import { navigateTo } from 'front-end/src/app/util/appHistory'
import { getFocusStyle } from '../../chakraThemeExtension'
import { useLooopTheme } from 'front-end/src/public-api/hooks/useLooopTheme'

type TabListType = {
  caption: string | JSX.Element
  paramName: string
  getComponent: () => any
  href?: string
  isActive?: boolean
  onClick?: (ev) => any
  icon?: React.ReactElement<any>
}

/**
 * @deprecated
 * Use TabList, Tab, Tabs from front-end/src/components
 */
export const TabsStatic: FC<{
  tabList: Array<Omit<TabListType, 'getComponent' | 'paramName'>>
  children: any
  lighterBg?: boolean
  dataTest?: string
}> = ({ dataTest = 'TabList', ...props }) => {
  const { theme } = useLooopTheme()

  return (
    <div className="TabsLegacy">
      <Box
        bgColor={
          props.lighterBg ? brandColors.grey['50'] : brandColors.grey['75']
        }
      >
        <div className="TabsLegacy__list" role="tablist" data-test={dataTest}>
          {props.tabList.map((tab, idx) => {
            let isActive = tab.isActive
            if (isActive === undefined) {
              isActive = tab.href === location.pathname
            }
            const url = tab.href
            return (
              <LinkOverlay
                className={classNames('TabsLegacy__list-item', {
                  selected: isActive
                })}
                key={idx}
                role="tab"
                aria-selected={isActive}
                onClick={tab.onClick}
                to={url || ''}
                borderBottom="1px solid transparent"
                _focusVisible={getFocusStyle(
                  theme.colors.buttonPrimary[600],
                  true
                )}
                _after={
                  isActive
                    ? {
                        content: '""',
                        backgroundColor: theme.colors.buttonPrimary[500],
                        width: '100%',
                        height: '2px',
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        left: 0
                      }
                    : undefined
                }
              >
                <Box
                  color={isActive ? theme.colors.buttonPrimary[500] : undefined}
                >
                  {tab.icon}
                  {tab.caption}
                </Box>
              </LinkOverlay>
            )
          })}
        </div>
      </Box>
      <div className="TabsLegacy__panel selected">{props.children}</div>
    </div>
  )
}

/**
 * @deprecated
 * TabList Legacy function which will generate Chakra Tabs and Panel from old tab object format
 */
const TabsLegacy: FC<
  { tabList: Array<TabListType> } & Omit<TabsProps, 'children'>
> = ({ tabList, className = 'TabsLegacy', ...rest }) => {
  const [activeTabIndex, setActiveTabIndex] = useState<number>()

  if (activeTabIndex === undefined) {
    const index = tabList.findIndex((tab) => tab.href === location.pathname)
    setActiveTabIndex(index >= 0 ? index : 0) // -1 if not found
  }

  if (!tabList) return <></>

  return (
    <Tabs className={className} defaultIndex={activeTabIndex} {...rest}>
      <TabList className={`${className}__list__wrapper`}>
        <Box className={`${className}__list`} role="tablist">
          {tabList.map((tab, idx) => {
            const isActive = activeTabIndex === idx
            return (
              <Tab
                className={classNames(`${className}__list-item`, {
                  selected: isActive
                })}
                key={idx}
                role="tab"
                aria-selected={isActive}
                onClick={() => {
                  setActiveTabIndex(idx)
                  tab.href && navigateTo(tab.href)
                }}
              >
                {tab.icon}
                {tab.caption}
              </Tab>
            )
          })}
        </Box>
      </TabList>

      <TabPanels className={`${className}__panels`}>
        {activeTabIndex
          ? tabList[activeTabIndex].getComponent()
          : tabList[0].getComponent()}
      </TabPanels>
    </Tabs>
  )
}

export default TabsLegacy
